import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Observable, scan, startWith } from 'rxjs';
import { PlanView } from 'src/app/admin/pages/plan-management/data/plan-management.models';
import { PlanNameCatalogService } from 'src/app/admin/pages/plan-management/data/plan-name-catalog.service';
import { NameCatalog, PENDING_CATALOG, startOfToday, toPlanCard } from 'src/app/admin/pages/plan-management/domain/plan-presentation';
import { TONE_TAG } from 'src/app/admin/pages/plan-management/plan-card/plan-card.component';

/** The names plan views do not carry (region, inspection type), filled in as each catalog answers. */
export function nameCatalog(names: PlanNameCatalogService, assetId: string, plans: readonly PlanView[]): Observable<NameCatalog> {
  return names.load(assetId, plans).pipe(
    scan((catalog, part) => ({ ...catalog, ...part }), PENDING_CATALOG),
    startWith(PENDING_CATALOG)
  );
}

/**
 * Plans to choose from before inserting inspections: what each is, how far along it is and
 * when it runs, so two plans with the same dates (a plan and its copy) can be told apart.
 */
@Component({
  selector: 'phdsoft-plan-choice-list',
  standalone: true,
  imports: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="plan-choice-list">
      @for (c of cards(); track c.plan.PlanId) {
        <button
          type="button"
          class="plan-choice"
          [class.plan-choice--selected]="c.plan.PlanId === selectedPlanId()"
          (click)="picked.emit(c.plan)"
        >
          <span class="plan-choice__head">
            <span class="plan-choice__title">{{ c.title }}</span>
            <span class="plan-choice__tag {{ tone[c.status.tone] }}">{{ c.status.label }}</span>
            @if (c.plan.PlanId === suggestedPlanId()) {
              <span class="plan-choice__tag plan-choice__tag--suggested">Suggested</span>
            }
          </span>
          <span class="plan-choice__line">
            Planned {{ c.target | date: 'mediumDate' }} · window {{ c.windowStart | date: 'mediumDate' }} –
            {{ c.windowEnd | date: 'mediumDate' }}
          </span>
          <span class="plan-choice__line">
            {{ c.progress.planned }} {{ c.progress.planned === 1 ? c.noun.one : c.noun.many }} ·
            {{ c.progress.executed }} executed · {{ c.progress.outlined }} to do
          </span>
          <span class="plan-choice__line plan-choice__line--muted">
            {{ c.origin }}@if (c.recurrence) { · {{ c.recurrence }} } · cycle {{ c.plan.CycleNumber }}@if (showRegion()) { · {{ c.regionName }} }
          </span>
        </button>
      }
    </div>
  `,
  styles: [`
    .plan-choice-list { display: flex; flex-direction: column; gap: 0.5rem; }
    .plan-choice {
      display: flex; flex-direction: column; gap: 0.25rem; width: 100%; padding: 0.625rem 0.75rem;
      text-align: left; cursor: pointer; color: inherit; font: inherit;
      background: var(--surface-card, transparent);
      border: 1px solid var(--surface-border, rgba(255, 255, 255, 0.2)); border-radius: 6px;
    }
    .plan-choice:hover, .plan-choice:focus-visible { border-color: var(--primary-color, #3b82f6); }
    .plan-choice--selected { border-color: var(--primary-color, #3b82f6); box-shadow: 0 0 0 1px var(--primary-color, #3b82f6); }
    .plan-choice__head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.375rem; }
    .plan-choice__title { font-weight: 600; margin-right: auto; }
    .plan-choice__tag { padding: 0.0625rem 0.5rem; border-radius: 999px; font-size: 0.75rem; white-space: nowrap; }
    .plan-choice__tag--suggested { border: 1px solid var(--primary-color, #3b82f6); }
    .plan-choice__line { font-size: 0.8125rem; }
    .plan-choice__line--muted { opacity: 0.7; }
  `]
})
export class PlanChoiceListComponent {
  readonly plans = input.required<readonly PlanView[]>();
  readonly catalog = input<NameCatalog | null>(PENDING_CATALOG);
  readonly suggestedPlanId = input<string | null>(null);
  readonly selectedPlanId = input<string | null | undefined>(null);
  /** The list mixes regions: say which each plan is in. */
  readonly showRegion = input(false);
  readonly picked = output<PlanView>();

  readonly tone = TONE_TAG;
  readonly cards = computed(() => {
    const today = startOfToday();
    const catalog = this.catalog() ?? PENDING_CATALOG;
    return this.plans().map(plan => toPlanCard(plan, catalog, today));
  });
}
